<template>
  <div class="analytics-dash">
    <div v-if="isLoading" class="analytics-loading">
      <BaseLoadingSpinner :with-overlay="false" :size="60" />
    </div>
    <div v-else-if="hasError" class="analytics-error">
      <p>Kunde inte ladda analysdata</p>
    </div>
    <template v-else-if="data">
      <div class="analytics-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="analytics-tab"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- SKÄL -->
      <div v-if="activeTab === 'reasons'" class="tab-content">
        <div class="reasons-row">
          <div
            v-if="category === 'ah' && data.ahReasons?.length"
            class="chart-card"
          >
            <h4 class="chart-title">Anledning till uthyrning</h4>
            <div class="reason-chart">
              <div
                v-for="reason in data.ahReasons"
                :key="'ah-' + reason.type"
                class="reason-bar-group"
              >
                <span class="reason-label">{{
                  ahReasonLabel(reason.type)
                }}</span>
                <div
                  class="reason-bar ah"
                  :style="{
                    width: reasonBarWidth(reason.count, data.ahReasons) + '%'
                  }"
                ></div>
                <span class="reason-count">{{ reason.count }}</span>
              </div>
            </div>
          </div>

          <div
            v-if="category === 'ov' && data.ovReasons?.length"
            class="chart-card"
          >
            <h4 class="chart-title">Anledning till överlåtelse</h4>
            <div class="reason-chart">
              <div
                v-for="reason in data.ovReasons"
                :key="'ov-' + reason.type"
                class="reason-bar-group"
              >
                <span class="reason-label">{{
                  ovReasonLabel(reason.type)
                }}</span>
                <div
                  class="reason-bar ov"
                  :style="{
                    width: reasonBarWidth(reason.count, data.ovReasons) + '%'
                  }"
                ></div>
                <span class="reason-count">{{ reason.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ÖVERVAKNING (AH only) -->
      <div v-if="activeTab === 'monitoring'" class="tab-content">
        <SubletDashboard />
      </div>

      <!-- RÖRELSEKARTA (BA only) -->
      <div v-if="activeTab === 'flowmap'" class="tab-content flowmap-content">
        <FlowMapTabs />
      </div>

      <!-- DETALJER -->
      <div v-if="activeTab === 'details'" class="tab-content details-content">
        <BaseLoadingSpinner
          v-if="detailsLoading"
          :with-overlay="false"
          :size="60"
        />
        <template v-else>
          <!-- BA -->
          <template v-if="category === 'ba'">
            <ChartCard
              gray
              title="Hur länge hyresgästen bott i lägenheten innan påbörjat byte"
              :subtitle="
                userStats?.avgYears
                  ? `(Genomsnitt <b>${userStats.avgYears}</b> år)`.replace(
                      '.',
                      ','
                    )
                  : ''
              "
              class="card-full-width"
              height500
            >
              <YearsBeforeChart
                :had-years-list="userStats.hadYearsList"
                :loading="loadingUserStats"
              />
            </ChartCard>
            <ChartCard gray title="Typ av byte">
              <SwapTypeChart
                :swap-type-counts="swapTypes.swapTypeCounts"
                :loading="loadingSwapTypes"
              />
            </ChartCard>
            <ChartCard
              gray
              title="Genomsnittlig handläggningstid per ankomstmånad"
              :subtitle="`Genomsnitt <b>${baProcessingYearAvg}</b> dagar`"
              class="card-full-width"
              height500
            >
              <div class="monthly-chart-wrap">
                <YearPicker
                  :years="baProcessingYears"
                  :model-value="baProcessingYear"
                  @update:model-value="baProcessingYear = $event"
                />
              </div>
              <ProcessingTimeChart
                :avg-processing-days="data?.baKuriosa?.avgProcessingDays"
                :selected-year="baProcessingYear"
                :loading="loadingGeneral"
              />
            </ChartCard>
            <ChartCard gray title="Kuriosa" class="card-full-width">
              <BaseLoadingSpinner v-if="loadingGeneral" />
              <div v-else class="kuriosa-grid">
                <div class="kuriosa-item">
                  <strong class="kuriosa-value">{{
                    general.avgMovingDistanceKm?.toString().replace('.', ',') ||
                    '–'
                  }}</strong>
                  <span class="kuriosa-label"
                    >km genomsnittlig flyttsträcka</span
                  >
                </div>
                <div class="kuriosa-item">
                  <strong class="kuriosa-value">{{
                    general.mostActiveArea || '–'
                  }}</strong>
                  <span class="kuriosa-label">mest aktiva område</span>
                </div>
                <div class="kuriosa-item">
                  <strong class="kuriosa-value">{{
                    general.mostPopularSwapMonth || '–'
                  }}</strong>
                  <span class="kuriosa-label">populäraste ankomstmånad</span>
                </div>
              </div>
            </ChartCard>
          </template>

          <!-- AH -->
          <template v-if="category === 'ah'">
            <ChartCard
              gray
              title="Genomsnittlig handläggningstid per ankomstmånad"
              :subtitle="`Genomsnitt <b>${ahProcessingYearAvg}</b> dagar`"
              class="card-full-width"
              height500
            >
              <div class="monthly-chart-wrap">
                <YearPicker
                  :years="ahProcessingYears"
                  :model-value="ahProcessingYear"
                  @update:model-value="ahProcessingYear = $event"
                />
              </div>
              <ProcessingTimeChart
                :avg-processing-days="data?.ahKuriosa?.avgProcessingDays"
                :selected-year="ahProcessingYear"
                :loading="false"
              />
            </ChartCard>
            <ChartCard gray title="Kuriosa" class="card-full-width">
              <div class="kuriosa-grid">
                <div class="kuriosa-item">
                  <strong class="kuriosa-value">{{
                    Math.round(data?.ahKuriosa?.avgRentalPeriodDays || 0)
                  }}</strong>
                  <span class="kuriosa-label"
                    >dagar genomsnittlig uthyrningsperiod</span
                  >
                </div>
                <div class="kuriosa-item">
                  <strong class="kuriosa-value">{{
                    data?.ahKuriosa?.mostPopularStartMonth || '–'
                  }}</strong>
                  <span class="kuriosa-label">vanligaste startmånad</span>
                </div>
                <div class="kuriosa-item">
                  <strong class="kuriosa-value"
                    >{{ furnishedStat.percent }}%</strong
                  >
                  <span class="kuriosa-label">{{ furnishedStat.label }}</span>
                </div>
                <div class="kuriosa-item">
                  <strong class="kuriosa-value"
                    >{{ data?.ahKuriosa?.extensionRate || 0 }}%</strong
                  >
                  <span class="kuriosa-label">förlänger minst en gång</span>
                </div>
                <div class="kuriosa-item">
                  <strong class="kuriosa-value">{{
                    data?.ahKuriosa?.longestChain || 0
                  }}</strong>
                  <span class="kuriosa-label">längsta förlängningskedja</span>
                </div>
                <div
                  v-if="data?.ahKuriosa?.avgFirstHandRent"
                  class="kuriosa-item"
                >
                  <strong class="kuriosa-value">{{
                    formatRent(data.ahKuriosa.avgFirstHandRent)
                  }}</strong>
                  <span class="kuriosa-label">snitt förstahandshyra</span>
                </div>
              </div>
            </ChartCard>
          </template>

          <!-- OV -->
          <template v-if="isOvCategory">
            <ChartCard
              gray
              title="Genomsnittlig handläggningstid per ankomstmånad"
              :subtitle="`Genomsnitt <b>${ovProcessingYearAvg}</b> dagar`"
              class="card-full-width"
              height500
            >
              <div class="monthly-chart-wrap">
                <YearPicker
                  :years="ovProcessingYears"
                  :model-value="ovProcessingYear"
                  @update:model-value="ovProcessingYear = $event"
                />
              </div>
              <ProcessingTimeChart
                :avg-processing-days="ovKuriosaCurrent.avgProcessingDays"
                :selected-year="ovProcessingYear"
                :loading="false"
              />
            </ChartCard>
            <ChartCard gray title="Kuriosa" class="card-full-width">
              <div class="kuriosa-grid">
                <div class="kuriosa-item">
                  <strong class="kuriosa-value">{{
                    ovKuriosaCurrent.mostPopularTransferMonth || '–'
                  }}</strong>
                  <span class="kuriosa-label"
                    >populäraste överlåtelsemånad</span
                  >
                </div>
                <div v-if="category === 'ovCompany'" class="kuriosa-item">
                  <strong class="kuriosa-value"
                    >{{ data?.ovKuriosa?.company?.brokerRate || 0 }}%</strong
                  >
                  <span class="kuriosa-label">går via mäklare</span>
                </div>
                <div
                  v-if="
                    category === 'ovCompany' &&
                    data?.ovKuriosa?.company?.top3BrokerConcentration != null
                  "
                  class="kuriosa-item"
                >
                  <strong class="kuriosa-value"
                    >{{
                      data.ovKuriosa.company.top3BrokerConcentration
                    }}%</strong
                  >
                  <span class="kuriosa-label"
                    >av mäklarärenden via topp 3 mäklare</span
                  >
                </div>
              </div>
            </ChartCard>
          </template>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import {
  getDashboardAnalytics,
  getDashboardStatistics
} from '@/services/baService';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';
import FlowMapTabs from '@/components/FlowMapTabs.vue';
import SubletDashboard from '@/components/SubletDashboard.vue';
import ChartCard from '@/components/ChartCard.vue';
import YearsBeforeChart from '@/components/Charts/YearsBeforeChart.vue';
import SwapTypeChart from '@/components/Charts/SwapTypeChart.vue';
import ProcessingTimeChart from '@/components/Charts/ProcessingTimeChart.vue';
import YearPicker from '@/components/YearPicker.vue';

function avgForYear(avgByYear, selectedYear) {
  if (!avgByYear) return 0;
  let sum = 0;
  let count = 0;
  for (const [year, yearData] of Object.entries(avgByYear)) {
    if (selectedYear !== 'all' && year !== selectedYear) continue;
    for (const v of Object.values(yearData || {})) {
      const num = Number(v);
      if (!Number.isFinite(num)) continue;
      sum += num;
      count += 1;
    }
  }
  return count ? Math.round(sum / count) : 0;
}

const AH_REASON_LABELS = {
  arbete: 'Arbete',
  studie: 'Studier',
  sambo: 'Sambo',
  utlandsvistelse: 'Utlandsvistelse',
  other: 'Övrigt',
  null: 'Ej angivet'
};

const OV_REASON_LABELS = {
  sambo: 'Samboende',
  separation: 'Separation',
  other: 'Övrigt',
  null: 'Ej angivet'
};

export default {
  components: {
    BaseLoadingSpinner,
    FlowMapTabs,
    SubletDashboard,
    ChartCard,
    YearsBeforeChart,
    SwapTypeChart,
    ProcessingTimeChart,
    YearPicker
  },
  props: {
    category: { type: String, default: 'ba' }
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      activeTab: 'reasons',
      data: null,
      baseTabs: [
        { id: 'reasons', label: 'Skäl till flytt' },
        { id: 'details', label: 'Detaljer' }
      ],
      detailsLoading: false,
      general: {},
      swapTypes: {},
      userStats: {},
      loadingGeneral: false,
      loadingSwapTypes: false,
      loadingUserStats: false,
      baProcessingYear: 'all',
      ahProcessingYear: 'all',
      ovProcessingYear: 'all'
    };
  },
  computed: {
    tabs() {
      let tabs = [...this.baseTabs];
      if (this.category === 'ba') {
        tabs = tabs.filter(t => t.id !== 'reasons');
        tabs.push({ id: 'flowmap', label: 'Rörelsekarta' });
      }
      if (this.category === 'ah') {
        tabs.push({ id: 'monitoring', label: 'Övervakning' });
      }
      return tabs;
    },
    isOvCategory() {
      return this.category === 'ovPrivate' || this.category === 'ovCompany';
    },
    ovKuriosaCurrent() {
      const k = this.data?.ovKuriosa;
      if (!k) return {};
      return this.category === 'ovCompany' ? k.company || {} : k.private || {};
    },
    furnishedStat() {
      const k = this.data?.ahKuriosa;
      if (!k) return { percent: 0, label: 'hyr ut möblerat' };
      const total = (k.furnished || 0) + (k.unfurnished || 0);
      if (!total) return { percent: 0, label: 'hyr ut möblerat' };
      if (k.furnished >= k.unfurnished) {
        return {
          percent: Math.round((k.furnished / total) * 100),
          label: 'hyr ut möblerat'
        };
      }
      return {
        percent: Math.round((k.unfurnished / total) * 100),
        label: 'hyr ut omöblerat'
      };
    },
    baProcessingYears() {
      return Object.keys(this.data?.baKuriosa?.avgProcessingDays || {}).sort();
    },
    baProcessingYearAvg() {
      return avgForYear(
        this.data?.baKuriosa?.avgProcessingDays,
        this.baProcessingYear
      );
    },
    ahProcessingYears() {
      return Object.keys(this.data?.ahKuriosa?.avgProcessingDays || {}).sort();
    },
    ahProcessingYearAvg() {
      return avgForYear(
        this.data?.ahKuriosa?.avgProcessingDays,
        this.ahProcessingYear
      );
    },
    ovProcessingYears() {
      return Object.keys(this.ovKuriosaCurrent?.avgProcessingDays || {}).sort();
    },
    ovProcessingYearAvg() {
      return avgForYear(
        this.ovKuriosaCurrent?.avgProcessingDays,
        this.ovProcessingYear
      );
    }
  },
  watch: {
    activeTab(val) {
      if (val === 'details') this.fetchDetailsData();
    },
    category() {
      this.activeTab = this.tabs[0]?.id || 'details';
      if (this.activeTab === 'details') this.fetchDetailsData();
    }
  },
  async mounted() {
    await this.fetchData();
    if (this.activeTab === 'details') this.fetchDetailsData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        this.data = await getDashboardAnalytics();
      } catch (error) {
        console.error('Analytics error:', error);
        this.hasError = true;
      }
      this.isLoading = false;
    },
    async fetchDetailsData() {
      if (this.category !== 'ba') return;
      if (this.loadingGeneral || this.general?.approved != null) return;
      this.detailsLoading = true;
      try {
        const stats = await getDashboardStatistics();
        const ba = stats?.ba || {};
        this.general = {
          ...(ba.counts || {}),
          avgMovingDistanceKm: ba.avgMovingDistanceKm,
          mostActiveArea: ba.mostActiveArea,
          mostPopularSwapMonth: ba.mostPopularSwapMonth
        };
        this.swapTypes = { swapTypeCounts: ba.swapTypeCounts };
        this.userStats = {
          avgYears: ba.avgYears,
          hadYearsList: ba.hadYearsList,
          movingWithinLandlord: ba.movingWithinLandlord
        };
      } catch (err) {
        console.error('Details fetch error:', err);
      }
      this.detailsLoading = false;
    },
    formatRent(val) {
      if (!val) return '–';
      return Number(val).toLocaleString('sv-SE') + ' kr';
    },
    reasonBarWidth(count, reasons) {
      const max = Math.max(...(reasons || []).map(r => r.count), 1);
      return Math.max(5, (count / max) * 80);
    },

    ahReasonLabel(type) {
      return (
        AH_REASON_LABELS[type] ||
        AH_REASON_LABELS[String(type)] ||
        type ||
        'Ej angivet'
      );
    },

    ovReasonLabel(type) {
      return (
        OV_REASON_LABELS[type] ||
        OV_REASON_LABELS[String(type)] ||
        type ||
        'Ej angivet'
      );
    }
  }
};
</script>

<style scoped>
.analytics-dash {
  background: #fff;
  border-radius: 12px;
  color: #2c3e50;
  overflow: hidden;
  min-height: 400px;
}

.example-data-banner {
  background: #fff8e1;
  border-bottom: 1px solid #f0e4b8;
  color: #8a6d00;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
  padding: 8px 16px;
}

.analytics-loading,
.analytics-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.analytics-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e8e6de;
  padding: 0 24px;
  background: #f8f7f7;
}

.analytics-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #878b9d;
  padding: 12px 20px;
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
}

.analytics-tab.active {
  color: #2c3e50;
  border-bottom-color: #2c3e50;
  font-weight: 700;
}

.tab-content {
  padding: 24px;
}

/* Stat cards */
.stat-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 18px;
}

.stat-card-label {
  font-size: 1rem;
  color: #878b9d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
}

.stat-card-value {
  font-size: 2.2rem;
  font-weight: 800;
  color: #2c3e50;
}

.stat-card-sub {
  font-size: 1.1rem;
  color: #878b9d;
  margin-top: 2px;
}

/* Chart layout */
.chart-row {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 16px;
}

.chart-card {
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 20px;
  margin-bottom: 16px;
}

.chart-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #878b9d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 16px 0;
}

/* Stacked bar chart */
.stacked-chart {
  display: flex;
  gap: 8px;
  height: 180px;
  align-items: flex-end;
}

.stacked-chart.tall {
  height: 260px;
}

.chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 30px;
  flex-shrink: 0;
}

.y-tick {
  font-size: 1rem;
  color: #878b9d;
  text-align: right;
}

.chart-bars {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.bar-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stacked-bar {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  max-width: 40px;
}

.bar-segment {
  width: 100%;
  min-height: 0;
  transition: height 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
  position: relative;
  cursor: default;
}

.donut-segment {
  transition: stroke-dasharray 0.8s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.bar-segment[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #2c3e50;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
}

.bar-label {
  font-size: 1rem;
  color: #878b9d;
  margin-top: 6px;
}

.chart-legend {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.1rem;
  color: #878b9d;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.donut-dot.ba,
.legend-dot.ba,
.bar-segment.ba {
  background: #409fff;
}

.legend-dot.ah,
.donut-dot.ah,
.bar-segment.ah {
  background: #05337f;
}
.donut-dot.ov-private,
.legend-dot.ov-private,
.bar-segment.ov-private {
  background: #f7bd9e;
}

.donut-dot.ov-company,
.legend-dot.ov-company,
.bar-segment.ov-company {
  background: #b98d75;
}

/* Donut chart */
.donut-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.donut-svg {
  width: 160px;
  height: 160px;
}

.donut-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.donut-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  color: #5a6370;
}

.donut-item strong {
  margin-left: auto;
  color: #2c3e50;
}

.donut-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Type cards */
.type-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.type-card {
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 20px;
}

.type-card-label {
  font-size: 1rem;
  color: #878b9d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.type-card-value {
  font-size: 2.8rem;
  font-weight: 800;
  color: var(--cat-ba);
}

.type-card-sub {
  font-size: 1.1rem;
  color: #878b9d;
}

/* Reason chart */
.reason-chart {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 10px 0;
}

.reason-bar-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reason-bar {
  height: 28px;
  background: var(--cat-ba);
  border-radius: 4px;
  transition: width 0.4s ease;
}

.reason-label {
  font-size: 1.2rem;
  color: #5a6370;
  min-width: 140px;
  text-align: right;
  order: -1;
}

.reason-count {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2c3e50;
  min-width: 30px;
}

/* Reasons row */
.reasons-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.counts-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.count-card {
  background: #f8f8fa;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 14px 16px;
}

.count-card-label {
  font-size: 1rem;
  color: #878b9d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.count-card-value {
  font-size: 1.8rem;
  font-weight: 800;
  color: var(--color-dark-blue);
}

.count-card-sub {
  font-size: 1rem;
  color: #878b9d;
  margin-top: 2px;
}

.reason-bar.ah {
  background: var(--cat-ah);
}

.reason-bar.ov {
  background: var(--cat-ov-private);
}

/* Session time */
.session-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}

.session-item {
  text-align: center;
  background: #eeedea;
  border-radius: 8px;
  padding: 16px 10px;
}

.session-label {
  font-size: 1rem;
  color: #878b9d;
  margin-bottom: 6px;
}

.session-value {
  font-size: 2rem;
  font-weight: 800;
  color: var(--cat-ba);
}

.session-sub {
  font-size: 1rem;
  color: #878b9d;
  margin-top: 2px;
}

.year-picker {
  display: flex;
  gap: 4px;
}

.year-btn {
  background: #f8f8fa;
  border: 1px solid #e6e6e6;
  color: #878b9d;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.year-btn.active {
  background: var(--color-dark-blue);
  color: #fff;
  border-color: var(--color-dark-blue);
}
</style>
